/* DripPerformancePanel — Admin → Services tab.
 *
 * Closes the loop on the 3-stage abandoned cart drip: surfaces emails
 * sent / opened / clicked / coupons redeemed / revenue attributed per
 * stage so admins can A/B-test stage timings and subject lines from
 * the dashboard.
 *
 * Data source: GET /api/store/admin/drip/performance?days=N
 *   • email_logs (kind: abandoned_cart_s{1,2,3}) + Resend webhook fields
 *   • coupon_redemptions joined to orders for revenue attribution
 *
 * Auth: store-admin JWT (same as VariantImageEditor).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, RefreshCw, Mail, Eye, MousePointer2, Tag, DollarSign,
  AlertTriangle, ChevronDown, Lock,
} from 'lucide-react';
import DripStageEditor from './DripStageEditor';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const TOKEN_KEY = 'zhelta_store_token';

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
function setToken(v) {
  try { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

async function authFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  return fetch(`${API}${path}`, { ...options, headers });
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StageRow({ row, totalRevenue }) {
  const sharePct = totalRevenue > 0 ? Math.round((row.revenue_attributed / totalRevenue) * 100) : 0;
  const tone = row.stage === 1 ? 'border-emerald-200 bg-emerald-50/40'
    : row.stage === 2 ? 'border-amber-200 bg-amber-50/40'
    : 'border-rose-200 bg-rose-50/40';
  const stageColor = row.stage === 1 ? 'text-emerald-700' : row.stage === 2 ? 'text-amber-700' : 'text-rose-700';
  return (
    <div
      className={`p-4 rounded-xl border ${tone}`}
      data-testid={`drip-stage-${row.stage}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
        <div>
          <span className={`text-[10px] font-bold uppercase tracking-[0.22em] ${stageColor}`}>Stage {row.stage}</span>
          <h4 className="text-[14px] font-bold text-gray-900">{row.subject_template}</h4>
        </div>
        <div className="text-right">
          <div className="text-[18px] font-extrabold text-gray-900">{fmtMoney(row.revenue_attributed)}</div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">attributed revenue · {sharePct}% of drip</div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-[11.5px] mt-3">
        <Metric icon={<Mail className="w-3 h-3" />} label="Sent" value={row.sent_total} sub={row.sent_mocked > 0 ? `${row.sent_mocked} mocked` : null} />
        <Metric icon={<Eye className="w-3 h-3" />} label="Opened" value={row.opened} sub={`${row.open_rate}%`} />
        <Metric icon={<MousePointer2 className="w-3 h-3" />} label="Clicked" value={row.clicked} sub={`${row.click_rate}%`} />
        <Metric icon={<Tag className="w-3 h-3" />} label="Coupons used" value={row.coupons_redeemed} sub={`of ${row.coupons_minted}`} />
        <Metric icon={<DollarSign className="w-3 h-3" />} label="Discount given" value={fmtMoney(row.discount_given)} small />
        <Metric icon={<TrendingUp className="w-3 h-3" />} label="Rev / send" value={fmtMoney(row.revenue_per_send)} small />
      </div>
    </div>
  );
}

function Metric({ icon, label, value, sub, small }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[9.5px] uppercase tracking-[0.16em] text-gray-500 inline-flex items-center gap-1">
        {icon}{label}
      </span>
      <span className={`font-extrabold text-gray-900 ${small ? 'text-[12px]' : 'text-[15px]'}`}>{value}</span>
      {sub && <span className="text-[10px] text-gray-500">{sub}</span>}
    </div>
  );
}

function SignInForm({ onAuthed }) {
  const [email, setEmail] = useState('admin@zhelta.com');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/store/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const d = await r.json();
      if (!r.ok || !d.token) throw new Error(d.detail || 'Login failed');
      setToken(d.token);
      onAuthed();
    } catch (e2) {
      setErr(e2.message || 'Sign-in failed');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={onSubmit} className="px-6 py-6 flex flex-col gap-3" data-testid="drip-perf-signin">
      <div className="flex items-center gap-2 text-[12px] text-gray-600">
        <Lock className="w-3.5 h-3.5 text-[#c9a227]" /> Sign in with a store-admin account.
      </div>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]" />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]" />
      {err && <span className="text-[11.5px] text-rose-700"><AlertTriangle className="w-3 h-3 inline mr-1" />{err}</span>}
      <button type="submit" disabled={busy || !password} className="px-4 py-2 rounded-full font-bold uppercase tracking-wider text-[11px] text-zinc-950 disabled:opacity-50" style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

export default function DripPerformancePanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [authedTick, setAuthedTick] = useState(0);

  const isAuthed = useMemo(() => !!getToken(), [authedTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch(`/store/admin/drip/performance?days=${days}`);
      if (r.status === 401) {
        setToken(''); setAuthedTick((t) => t + 1); setData(null); return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { if (isAuthed) load(); }, [isAuthed, load]);

  const totals = data?.totals || {};

  return (
    <section
      data-testid="admin-drip-performance"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-gradient-to-r from-zinc-950 to-zinc-800 text-white flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#f5a623]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Drip · Performance</div>
            <h3 className="text-[15px] font-bold">Abandoned cart funnel · per stage</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="drip-perf-days"
              className="appearance-none pl-3 pr-7 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold uppercase tracking-wider transition-colors"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 365 days</option>
            </select>
            <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/70" />
          </div>
          {isAuthed && (
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="drip-perf-reload"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Reload
            </button>
          )}
        </div>
      </header>

      {!isAuthed ? (
        <SignInForm onAuthed={() => setAuthedTick((t) => t + 1)} />
      ) : (
        <>
          {/* Totals strip */}
          <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-5 gap-3 bg-[#fffbe9] border-b border-[#f0e3a8]">
            <Total label="Emails sent" value={totals.sent ?? '–'} testid="drip-total-sent" />
            <Total label="Open rate" value={`${totals.open_rate ?? 0}%`} sub={`${totals.opened ?? 0} opens`} testid="drip-total-open-rate" />
            <Total label="Click rate" value={`${totals.click_rate ?? 0}%`} sub={`${totals.clicked ?? 0} clicks`} testid="drip-total-click-rate" />
            <Total label="Coupons used" value={totals.redeemed ?? 0} sub={`${totals.redeem_rate ?? 0}% redeem rate`} testid="drip-total-redeemed" />
            <Total
              label="Revenue attributed"
              value={fmtMoney(totals.revenue ?? 0)}
              sub={`${fmtMoney(totals.revenue_per_send ?? 0)} / send`}
              testid="drip-total-revenue"
              tone="gold"
            />
          </div>

          {!data?.resend_ready && (
            <div className="mx-6 mt-3 px-3 py-2 rounded-md text-[11.5px] bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span><strong>Resend not configured</strong> — emails are mocked. Engagement metrics will only populate once <code>RESEND_API_KEY</code> is set.</span>
            </div>
          )}

          {err && (
            <div className="mx-6 my-3 px-3 py-2 rounded-md text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> Failed to load: {err}
            </div>
          )}

          {/* Per-stage rows */}
          <div className="p-4 flex flex-col gap-3" data-testid="drip-stage-list">
            {(data?.stages || []).map((row) => (
              <StageRow key={row.stage} row={row} totalRevenue={totals.revenue || 0} />
            ))}
            {(!data?.stages || data.stages.length === 0) && !loading && (
              <div className="text-center py-8 text-gray-500 text-[13px]">No drip data in this window yet.</div>
            )}
          </div>

          {/* Inline live editor for stage timing/subject/coupon */}
          <DripStageEditor />
        </>
      )}
    </section>
  );
}

function Total({ label, value, sub, testid, tone }) {
  const valueClass = tone === 'gold' ? 'text-[#a8871a]' : 'text-zinc-950';
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className={`text-[16px] sm:text-[18px] font-extrabold ${valueClass} truncate`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
    </div>
  );
}
